import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, MailOpen, Reply, Archive, Inbox, MessageSquare } from 'lucide-react';
import { contactsApi } from '../../api/endpoints/contacts';
import { DataTable, type Column } from '../../components/ui/DataTable';
import { Button } from '../../components/ui/Button';
import { Select } from '../../components/ui/Select';
import { SearchInput } from '../../components/ui/SearchInput';
import { StatCard } from '../../components/ui/StatCard';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Textarea } from '../../components/ui/Textarea';
import { useToast } from '../../components/ui/Alert';
import { PermissionGate } from '../../components/ui/PermissionGate';
import { useDebounce } from '../../hooks/useDebounce';
import { usePagination } from '../../hooks/usePagination';
import { PERMISSIONS } from '../../lib/permissions';
import { formatDate, formatRelativeTime } from '../../lib/formatters';
import type { ContactSubmission, ContactStatus } from '../../types/contact';

const STATUS_OPTIONS: ContactStatus[] = ['unread', 'read', 'replied', 'archived'];

const STATUS_VARIANTS: Record<ContactStatus, 'default' | 'info' | 'success' | 'warning'> = {
  unread: 'warning',
  read: 'info',
  replied: 'success',
  archived: 'default',
};

export function ContactInbox() {
  const { storeId } = useParams<{ storeId: string }>();
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search);
  const [statusFilter, setStatusFilter] = useState('');
  const { page, limit, goToPage } = usePagination({
    resetDeps: [debouncedSearch, statusFilter],
  });
  const [replyTarget, setReplyTarget] = useState<ContactSubmission | null>(null);
  const [replyText, setReplyText] = useState('');
  const [viewTarget, setViewTarget] = useState<ContactSubmission | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['contacts', storeId, { page, limit, search: debouncedSearch, status: statusFilter }],
    queryFn: () => contactsApi.list(storeId!, {
      page,
      limit,
      search: debouncedSearch || undefined,
      status: statusFilter || undefined,
    }),
    enabled: !!storeId,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { status?: string; reply?: string } }) =>
      contactsApi.update(storeId!, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts', storeId] });
    },
    onError: () => toast('error', t('common.error')),
  });

  const handleReply = () => {
    if (!replyTarget || !replyText.trim()) return;
    updateMutation.mutate(
      { id: replyTarget._id, data: { reply: replyText, status: 'replied' } },
      {
        onSuccess: () => {
          toast('success', t('contactInbox.replySuccess'));
          setReplyTarget(null);
          setReplyText('');
        },
      },
    );
  };

  const handleMarkRead = (contact: ContactSubmission) => {
    updateMutation.mutate({ id: contact._id, data: { status: 'read' } });
  };

  const handleArchive = (contact: ContactSubmission) => {
    updateMutation.mutate({ id: contact._id, data: { status: 'archived' } });
  };

  const columns: Column<ContactSubmission>[] = [
    {
      key: 'from',
      header: t('contactInbox.from'),
      render: (c) => (
        <div
          className="cursor-pointer"
          onClick={() => { setViewTarget(c); if (c.status === 'unread') handleMarkRead(c); }}
        >
          <div className="flex items-center gap-2">
            {c.status === 'unread' ? (
              <Mail className="h-4 w-4 text-accent" />
            ) : (
              <MailOpen className="h-4 w-4 text-text-muted" />
            )}
            <span className={c.status === 'unread' ? 'font-semibold' : 'font-medium'}>{c.name}</span>
          </div>
          <p className="text-xs text-text-muted ms-6">{c.email}</p>
        </div>
      ),
    },
    {
      key: 'subject',
      header: t('contactInbox.subject'),
      render: (c) => (
        <div className="cursor-pointer" onClick={() => setViewTarget(c)}>
          <p className={`text-sm ${c.status === 'unread' ? 'font-semibold' : ''}`}>{c.subject || '-'}</p>
          <p className="text-xs text-text-muted truncate max-w-[200px]">{c.message}</p>
        </div>
      ),
    },
    {
      key: 'status',
      header: t('common.status'),
      render: (c) => (
        <Badge variant={STATUS_VARIANTS[c.status]}>
          {t(`contactInbox.${c.status}`)}
        </Badge>
      ),
    },
    {
      key: 'date',
      header: t('common.date'),
      sortable: true,
      render: (c) => <span className="text-xs text-text-secondary">{formatRelativeTime(c.createdAt)}</span>,
    },
    {
      key: 'actions',
      header: '',
      render: (c) => (
        <PermissionGate requires={PERMISSIONS.CONTACTS_EDIT}>
          <div className="flex items-center gap-1">
            <button
              onClick={() => { setReplyTarget(c); setReplyText(''); }}
              className="rounded-lg p-1.5 text-text-muted hover:bg-bg-hover hover:text-accent"
              title={t('contactInbox.reply')}
            >
              <Reply className="h-4 w-4" />
            </button>
            {c.status !== 'archived' && (
              <button
                onClick={() => handleArchive(c)}
                className="rounded-lg p-1.5 text-text-muted hover:bg-bg-hover hover:text-warning"
                title={t('contactInbox.archive')}
              >
                <Archive className="h-4 w-4" />
              </button>
            )}
          </div>
        </PermissionGate>
      ),
    },
  ];

  const contacts = data?.contacts ?? [];
  const total = data?.pagination?.total ?? 0;
  const totalPages = data?.pagination?.pages ?? 1;
  const unreadCount = contacts.filter(c => c.status === 'unread').length;

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl font-bold">{t('contactInbox.title')}</h1>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mb-6">
        <StatCard
          title={t('contactInbox.totalMessages')}
          value={total}
          icon={<MessageSquare className="h-5 w-5" />}
        />
        <StatCard
          title={t('contactInbox.unreadCount')}
          value={unreadCount}
          icon={<Inbox className="h-5 w-5" />}
        />
      </div>

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder={t('common.search') + '...'}
          className="max-w-sm"
        />
        <Select
          options={[
            { value: '', label: t('contactInbox.allStatuses') },
            ...STATUS_OPTIONS.map(s => ({ value: s, label: t(`contactInbox.${s}`) })),
          ]}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        />
      </div>

      <div className="rounded-xl border border-border-default bg-bg-secondary shadow-sm overflow-hidden">
        <DataTable
          columns={columns}
          data={contacts}
          isLoading={isLoading}
          keyExtractor={(c) => c._id}
          emptyTitle={t('contactInbox.empty')}
          page={page}
          totalPages={totalPages}
          total={total}
          limit={limit}
          onPageChange={goToPage}
        />
      </div>

      {/* View Message Modal */}
      <Modal
        isOpen={!!viewTarget}
        onClose={() => setViewTarget(null)}
        title={viewTarget?.subject || t('contactInbox.message')}
        size="lg"
      >
        {viewTarget && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent/20 text-accent font-bold text-sm">
                {viewTarget.name.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="font-medium">{viewTarget.name}</p>
                <p className="text-xs text-text-muted">{viewTarget.email}</p>
              </div>
              <span className="ms-auto text-xs text-text-muted">{formatDate(viewTarget.createdAt)}</span>
            </div>
            <div className="rounded-lg bg-bg-tertiary p-4">
              <p className="text-sm whitespace-pre-wrap">{viewTarget.message}</p>
            </div>
            {viewTarget.reply && (
              <div className="rounded-lg border border-accent/20 bg-accent/5 p-4">
                <p className="text-xs font-medium text-accent mb-1">{t('contactInbox.reply')}</p>
                <p className="text-sm whitespace-pre-wrap">{viewTarget.reply}</p>
                {viewTarget.repliedAt && (
                  <p className="text-xs text-text-muted mt-2">{formatDate(viewTarget.repliedAt)}</p>
                )}
              </div>
            )}
            {!viewTarget.reply && (
              <Button
                size="sm"
                onClick={() => { setReplyTarget(viewTarget); setReplyText(''); setViewTarget(null); }}
              >
                <Reply className="h-4 w-4" />
                {t('contactInbox.reply')}
              </Button>
            )}
          </div>
        )}
      </Modal>

      {/* Reply Modal */}
      <Modal
        isOpen={!!replyTarget}
        onClose={() => setReplyTarget(null)}
        title={`${t('contactInbox.reply')} - ${replyTarget?.name}`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setReplyTarget(null)}>{t('common.cancel')}</Button>
            <Button
              isLoading={updateMutation.isPending}
              onClick={handleReply}
              disabled={!replyText.trim()}
            >
              {t('contactInbox.reply')}
            </Button>
          </>
        }
      >
        {replyTarget && (
          <div className="space-y-4">
            <div className="rounded-lg bg-bg-tertiary p-3">
              <p className="text-xs text-text-muted mb-1">{t('contactInbox.message')}:</p>
              <p className="text-sm">{replyTarget.message}</p>
            </div>
            <Textarea
              id="reply"
              label={t('contactInbox.reply')}
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder={t('contactInbox.replyPlaceholder')}
              rows={4}
            />
          </div>
        )}
      </Modal>
    </div>
  );
}
